import { OutputData } from "@editorjs/editorjs";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { BusinessCategory } from "src/entities/BusinessCategory";
import { CountryPath } from "src/entities/CountryPath";
import { GetArticlesResponse } from "src/entities/GetArticlesResponse";
import { CreateArticleResponse } from "src/entities/v3/CreateArticleResponse";
import uploadImageByFile from "src/libs/image/post-image-by-image";
import { createHtmlBody } from "src/utils/editorjs/createHtmlBody";
import isPngOrJpeg from "src/utils/isPngOrJpeg";
import { usePost } from "./usePost";

interface Props {
  input: {
    publisherId: string;
    title: string;
    outputBody: OutputData;
    country: CountryPath;
    acceptedFiles: readonly File[];
    publishedAt: DateTime;
    businessCategories: BusinessCategory[];
    selectedCategories: string[];
    summary: string;
    companyInfo: string;
    mediaContact: string;
    useThumbnailAsFeaturedImage: boolean;
    currentThumbnailUrl?: string; // For duplicated articles
  };
  functions: {
    handleBackDropOpen: () => void;
    handleClose: () => void;
    handleScheduleClose: () => void;
    handleBackdropClose: () => void;
    handleSave: () => Promise<void>;
    handleComplete: (response: GetArticlesResponse) => void;
  };
}

type ArticleState = "PUBLISHED" | "DRAFT" | "SCHEDULED";

export const useCreateArticle = ({ input, functions }: Props) => {
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  /**
   * Post the article hooks
   */
  const { post } = usePost<CreateArticleResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles`,
    params: {
      title: input.title,
      body: input.outputBody,
      state: "published",
      summary: input.summary,
      companyInfo: input.companyInfo,
      mediaContact: input.mediaContact,
      country: input.country,
      businessCategories: [],
      htmlBody: "",
      thumbnailImageUrl: "",
      publishedAt: "",
    },
  });

  /**
   * Create the article
   */
  const createArticle = async (state: ArticleState) => {
    try {
      if (input.title.length === 0) {
        console.error(t("hooks.no_title"));
        enqueueSnackbar(t("hooks.set_title"), {
          variant: "error",
        });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      if (input.selectedCategories.length === 0) {
        console.error(t("hooks.no_business_categories"));
        enqueueSnackbar(t("hooks.set_business_categories"), {
          variant: "error",
        });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      if (
        !input.acceptedFiles[0] &&
        !input.currentThumbnailUrl &&
        state === "PUBLISHED"
      ) {
        enqueueSnackbar(t("hooks.set_thumbnail"), { variant: "error" });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      if (input.acceptedFiles[0] && !isPngOrJpeg(input.acceptedFiles[0])) {
        console.error(t("hooks.upload_image_file"));
        enqueueSnackbar(t("hooks.upload_image_file"), { variant: "error" });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      // Only upload if there's a new file, otherwise use existing thumbnail URL
      let thumbnailUrl = input.currentThumbnailUrl || "";

      if (input.acceptedFiles[0]) {
        try {
          const data = await uploadImageByFile(input.acceptedFiles[0], () => {
            console.error(t("hooks.image_upload_failed"));
            enqueueSnackbar(t("hooks.image_upload_failed"), {
              variant: "error",
            });
          });

          if (!data?.url) {
            functions.handleClose();
            functions.handleScheduleClose();
            functions.handleBackdropClose();
            return;
          }

          thumbnailUrl = data.url;
        } catch {
          functions.handleClose();
          functions.handleScheduleClose();
          functions.handleBackdropClose();
          return;
        }
      } else if (!input.currentThumbnailUrl && state === "PUBLISHED") {
        // No file and no existing thumbnail for published article - this shouldn't happen
        // as we check above, but just in case
        enqueueSnackbar(t("hooks.set_thumbnail"), { variant: "error" });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      const htmlBody = createHtmlBody(input.outputBody, input.summary);

      // Determine the state based on publishedAt
      const articleState =
        state === "PUBLISHED"
          ? input.publishedAt > DateTime.local()
            ? "scheduled"
            : "published"
          : "draft";

      // Use current time + 10 seconds for immediate publishing, otherwise use user-specified time
      const actualPublishedAt =
        articleState === "published"
          ? DateTime.now().plus({ seconds: 10 })
          : input.publishedAt;

      post({
        params: {
          title: input.title,
          publishedAt: actualPublishedAt.toUTC().toISO(),
          body: JSON.stringify(input.outputBody),
          htmlBody: htmlBody,
          thumbnailImageUrl: thumbnailUrl || null,
          state: articleState,
          businessCategories: input.selectedCategories,
          summary: input.summary,
          companyInfo: input.companyInfo,
          mediaContact: input.mediaContact,
          country: input.country,
          useThumbnailAsFeaturedImage: input.useThumbnailAsFeaturedImage,
        },
        onSuccess: (response) => {
          if (state === "PUBLISHED") {
            enqueueSnackbar(t("hooks.successfully_published"), {
              variant: "success",
            });
            // If response is null (successful creation), navigate to dashboard
            if (!response) {
              navigate("/dashboard");
              return;
            }
            // Convert v3 response to v1 format for compatibility
            // Use response id as slug, and use input data for other fields
            const v3Response = response as unknown as CreateArticleResponse;
            const v1Response: GetArticlesResponse = {
              data: {
                title: input.title,
                published_at: input.publishedAt.toUTC().toISO(),
                body: input.outputBody,
                meta_data: {
                  business_categories: [], // Not available in v3 response
                },
                publisherName: "", // Not available in v3 response
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                publisher: {} as any, // Not available in v3 response
                slug: v3Response.id, // Use id as slug for v3
                state: articleState,
                summary: input.summary,
                company_info: input.companyInfo,
                media_contact: input.mediaContact,
                use_thumbnail_as_featured_image:
                  input.useThumbnailAsFeaturedImage,
                thumbnail_image_url: thumbnailUrl || "",
                created_at: DateTime.now().toUTC().toISO(),
                search_words: [], // Not available in v3 response
              },
            };
            functions.handleComplete(v1Response);
          } else {
            enqueueSnackbar(t("hooks.successfully_saved_as_draft"), {
              variant: "success",
            });
            navigate("/dashboard");
          }
        },
        onError: () => {
          enqueueSnackbar(t("hooks.create_failed"), { variant: "error" });
        },
        onStart: () => {
          functions.handleBackDropOpen();
        },
        onEnd: () => {
          functions.handleBackdropClose();
          functions.handleScheduleClose();
          functions.handleClose();
        },
      });
    } catch (e) {
      if (e.message !== "") {
        enqueueSnackbar(e.message, { variant: "error" });
      } else {
        enqueueSnackbar(t("hooks.create_failed"), { variant: "error" });
      }
    }
  };

  return { createArticle };
};
